import React, { useState, useEffect } from "react";
import {
  Button,
  FileInput,
  Group,
  Image,
  Loader,
  Modal,
  Text,
  TextInput,
} from "@mantine/core";
import { useMutation, useQuery } from "@apollo/client";
import { notifications } from "@mantine/notifications";
import styled from "styled-components";
import { IconTrash, IconGripVertical } from "@tabler/icons-react";

import { useAdmin } from "../hooks/use-admin";
import { UPDATE_ADMIN_PASSWORD } from "../gqls/admin";
import {
  BANNERS,
  CREATE_BANNER,
  DELETE_BANNER,
  UPDATE_BANNER,
  UPDATE_BANNERS_ORDER,
} from "../gqls/banner";
import { REFERENCES, UPDATE_CREATE_REFERENCE } from "../gqls/reference";
import {
  CREATE_TOP_BANNER,
  DELETE_TOP_BANNER,
  TOP_BANNERS,
  UPDATE_TOP_BANNER,
} from "../gqls/top-banner";

export const HomePage = () => {
  const { admin } = useAdmin();

  const [updateBannersOrder] = useMutation(UPDATE_BANNERS_ORDER);
  const [draggedId, setDraggedId] = useState(null);
  const [draggedOverId, setDraggedOverId] = useState(null);
  const [localBanners, setLocalBanners] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);

  const {
    data: referencesData,
    refetch: referencesRefetch,
    loading: referencesLoading,
  } = useQuery(REFERENCES);
  const logo = referencesData?.references?.find((item) => item.name === "logo");

  const {
    data: bannerData,
    loading: bannerLoading,
    refetch: bannerRefetch,
  } = useQuery(BANNERS, {
    variables: {
      where: { type: "main" },
    },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (bannerData?.banners) {
      const sorted = [...bannerData.banners].sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );
      setLocalBanners(sorted);
      setHasChanges(false);
    }
  }, [bannerData]);

  const {
    data: topBannerData,
    loading: topBannerLoading,
    refetch: topBannerRefetch,
  } = useQuery(TOP_BANNERS, {
    fetchPolicy: "network-only",
  });

  const [visibleUpdatePassword, setVisibleUpdatePassword] = useState(false);
  const [visibleUpdateBanner, setVisibleUpdateBanner] = useState(false);
  const [visibleUpdateLogo, setVisibleUpdateLogo] = useState(false);
  const [visibleTopBannerModal, setVisibleTopBannerModal] = useState(false);

  const [updateAdminPassword, { loading }] = useMutation(
    UPDATE_ADMIN_PASSWORD,
    {
      onCompleted: () => {
        setVisibleUpdatePassword(false);
        notifications.show({
          color: "green",
          title: "Пароль изменен",
        });
      },
      onError: (err) => {
        console.error(err);
        notifications.show({
          color: "red",
          title:
            err.message === "password-error"
              ? "Неправильный текущий пароль"
              : "Произошла ошибка, повторите еще раз",
        });
      },
    },
  );

  const [updateBanner] = useMutation(UPDATE_BANNER, {
    onCompleted: () => {
      setVisibleUpdateBanner(false);
      notifications.show({
        color: "green",
        title: "Баннер изменен",
      });
      bannerRefetch();
    },
    onError: (err) => {
      console.error(err);
      notifications.show({
        color: "red",
        title: "Произошла ошибка, повторите еще раз",
      });
    },
  });

  const [createBanner] = useMutation(CREATE_BANNER, {
    onCompleted: () => {
      setVisibleUpdateBanner(false);
      notifications.show({
        color: "green",
        title: "Баннер добавлен",
      });
      bannerRefetch();
    },
    onError: (err) => {
      console.error(err);
      notifications.show({
        color: "red",
        title: "Произошла ошибка, повторите еще раз",
      });
    },
  });

  const [deleteBanner, { loading: deleteBannerLoading }] = useMutation(
    DELETE_BANNER,
    {
      onCompleted: () => {
        notifications.show({
          color: "green",
          title: "Баннер удален",
        });
        bannerRefetch();
      },
      onError: (err) => {
        console.error(err);
        notifications.show({
          color: "red",
          title: "Произошла ошибка, повторите еще раз",
        });
      },
    },
  );

  const [updateCreateReference] = useMutation(UPDATE_CREATE_REFERENCE);

  const [createTopBanner] = useMutation(CREATE_TOP_BANNER, {
    onCompleted: () => {
      setVisibleTopBannerModal(false);
      notifications.show({
        color: "green",
        title: "TopBanner добавлен",
      });
      topBannerRefetch();
    },
    onError: (err) => {
      console.error(err);
      notifications.show({
        color: err.message === "top-banner-exists" ? "yellow" : "red",
        title:
          err.message === "top-banner-exists"
            ? "TopBanner уже существует"
            : "Произошла ошибка, повторите еще раз",
      });
    },
  });

  const [updateTopBanner, { loading: updateTopBannerLoading }] = useMutation(
    UPDATE_TOP_BANNER,
    {
      onCompleted: () => {
        setVisibleTopBannerModal(false);
        notifications.show({
          color: "green",
          title: "TopBanner изменен",
        });
        topBannerRefetch();
      },
      onError: (err) => {
        console.error(err);
        notifications.show({
          color: "red",
          title: "Произошла ошибка, повторите еще раз",
        });
      },
    },
  );

  const [deleteTopBanner, { loading: deleteTopBannerLoading }] = useMutation(
    DELETE_TOP_BANNER,
    {
      onCompleted: () => {
        notifications.show({
          color: "green",
          title: "TopBanner удален",
        });
        topBannerRefetch();
      },
      onError: (err) => {
        console.error(err);
        notifications.show({
          color: "red",
          title: "Произошла ошибка, повторите еще раз",
        });
      },
    },
  );

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");

  const [bannerTitle, setBannerTitle] = useState("");
  const [bannerSubtitle, setBannerSubtitle] = useState("");
  const [file, setFile] = useState(null);
  const [bannerUpdateLoading, setBannerUpdateLoading] = useState(false);
  const [bannerDefaultBackground, setBannerDefaultBackground] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoUpdateLoading, setLogoUpdateLoading] = useState(false);
  const [topBannerUrl, setTopBannerUrl] = useState("");
  const [topBannerFile, setTopBannerFile] = useState(null);
  const [topBannerDefaultImage, setTopBannerDefaultImage] = useState(null);
  const [topBannerSubmitLoading, setTopBannerSubmitLoading] = useState(false);

  const handleUpdatePassword = (e) => {
    e.preventDefault();
    if (newPassword !== repeatPassword) {
      notifications.show({
        color: "yellow",
        title: "Пароли не совпадают",
      });
      return;
    }
    updateAdminPassword({
      variables: { data: { currentPassword, newPassword } },
    });
  };

  const openBannerModal = (banner = null) => {
    setSelectedBanner(banner);
    setBannerTitle(banner?.title ?? "");
    setBannerSubtitle(banner?.subtitle ?? "");
    setBannerDefaultBackground(banner?.background ?? null);
    setFile(null);
    setVisibleUpdateBanner(true);
  };

  const handleUpdateBanner = async (e) => {
    e.preventDefault();
    setBannerUpdateLoading(true);
    let bannerBackground = bannerDefaultBackground;
    if (file) {
      const formData = new FormData();
      formData.append("file", file);
      const photoResponse = await fetch("/api/admin/upload", {
        method: "POST",
        body: formData,
        headers: { authorization: localStorage.getItem("accessToken") },
      }).then((res) => res.json());
      if (!photoResponse?.filename) {
        notifications.show({
          color: "red",
          title: "Произошла ошибка, повторите еще раз",
        });
        setBannerUpdateLoading(false);
        return;
      }
      bannerBackground = photoResponse.filename;
    }
    const variables = {
      data: {
        title: bannerTitle.trim(),
        subtitle: bannerSubtitle.trim(),
        background: bannerBackground,
      },
    };
    const mutation = selectedBanner?.id
      ? updateBanner({
          variables: {
            ...variables,
            where: { id: selectedBanner.id },
          },
        })
      : createBanner({
          variables: {
            data: {
              ...variables.data,
              type: "main",
            },
          },
        });

    mutation.finally(() => {
      setBannerUpdateLoading(false);
    });
  };

  const handleDeleteBanner = (banner) => {
    if (!banner?.id) return;
    if (window.confirm("Удалить баннер?")) {
      deleteBanner({ variables: { where: { id: banner.id } } });
    }
  };

  const handleDragStart = (e, id) => {
    setDraggedId(id);
  };

  const handleDragOver = (e, id) => {
    e.preventDefault();
    if (id !== draggedOverId) {
      setDraggedOverId(id);
    }
  };

  const handleDrop = (e, targetId) => {
    e.preventDefault();
    if (draggedId === targetId) return;

    const draggedIndex = localBanners.findIndex((b) => b.id === draggedId);
    const targetIndex = localBanners.findIndex((b) => b.id === targetId);

    const updated = [...localBanners];
    const [removed] = updated.splice(draggedIndex, 1);
    updated.splice(targetIndex, 0, removed);

    setLocalBanners(updated);
    setHasChanges(true);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDraggedOverId(null);
  };

  const handleSaveOrder = async () => {
    try {
      await updateBannersOrder({
        variables: {
          ids: localBanners.map((b) => b.id),
        },
      });
      notifications.show({
        title: "Порядок обновлен",
        color: "green",
      });
      setHasChanges(false);
      bannerRefetch();
    } catch (error) {
      console.error(error);
      notifications.show({
        title: "Произошла ошибка, повторите еще раз",
        color: "red",
      });
    }
  };

  const handleUpdateLogo = async (e) => {
    e.preventDefault();
    if (!logoFile) return;
    setLogoUpdateLoading(true);
    const formData = new FormData();
    formData.append("file", logoFile);
    formData.append("type", "png");
    const photoResponse = await fetch("/api/admin/upload", {
      method: "POST",
      body: formData,
      headers: { authorization: localStorage.getItem("accessToken") },
    }).then((res) => res.json());
    if (!photoResponse?.filename) {
      notifications.show({
        color: "red",
        title: "Произошла ошибка, повторите еще раз",
      });
      setLogoUpdateLoading(false);
      return;
    }
    const newLogoValue = photoResponse.filename;
    updateCreateReference({
      variables: {
        data: { name: "logo", value: newLogoValue },
      },
    })
      .then(() => {
        referencesRefetch();
        notifications.show({ color: "green", title: "Логотип изменен" });
        setVisibleUpdateLogo(false);
      })
      .catch(() => {
        notifications.show({
          color: "red",
          title: "Произошла ошибка, повторите еще раз",
        });
      })
      .finally(() => {
        setLogoUpdateLoading(false);
      });
  };

  const openTopBannerModal = () => {
    if (topBanner) {
      setTopBannerUrl(topBanner.url ?? "");
      setTopBannerDefaultImage(topBanner.image ?? null);
    } else {
      setTopBannerUrl("");
      setTopBannerDefaultImage(null);
    }
    setTopBannerFile(null);
    setVisibleTopBannerModal(true);
  };

  const handleTopBannerSubmit = async (e) => {
    e.preventDefault();
    setTopBannerSubmitLoading(true);

    let topBannerImage = topBannerDefaultImage;

    if (topBannerFile) {
      const formData = new FormData();
      formData.append("file", topBannerFile);
      const photoResponse = await fetch("/api/admin/upload", {
        method: "POST",
        body: formData,
        headers: { authorization: localStorage.getItem("accessToken") },
      }).then((res) => res.json());

      if (!photoResponse?.filename) {
        notifications.show({
          color: "red",
          title: "Произошла ошибка, повторите еще раз",
        });
        setTopBannerSubmitLoading(false);
        return;
      }

      topBannerImage = photoResponse.filename;
    }

    if (!topBannerImage) {
      notifications.show({
        color: "yellow",
        title: "Загрузите изображение",
      });
      setTopBannerSubmitLoading(false);
      return;
    }

    const variables = {
      data: {
        image: topBannerImage,
        url: topBannerUrl,
      },
    };

    const mutation = topBanner?.id
      ? updateTopBanner({
          variables: {
            ...variables,
            where: { id: topBanner.id },
          },
        })
      : createTopBanner({ variables });

    mutation.finally(() => {
      setTopBannerSubmitLoading(false);
    });
  };

  const handleDeleteTopBanner = () => {
    if (!topBanner?.id) return;
    if (window.confirm("Удалить TopBanner?")) {
      deleteTopBanner({ variables: { where: { id: topBanner.id } } });
    }
  };

  const banners = localBanners;
  const topBanner = topBannerData?.topBanners?.[0];

  return (
    <>
      <Group align="center" justify="space-between">
        <Text fw="600">Профиль</Text>
      </Group>
      <Group mt={12}>
        <Text size="sm">Email:</Text>
        <Text size="sm" fw="600">
          {admin.email}
        </Text>
      </Group>
      <div>
        <Button
          onClick={() => setVisibleUpdatePassword(true)}
          variant="outline"
          mt={16}
          size="xs"
        >
          Изменить пароль
        </Button>
      </div>
      <Group mt={20} align="center" justify="space-between">
        <Text fw="600">Баннеры</Text>
        <Group gap="xs">
          {hasChanges && (
            <Button onClick={handleSaveOrder} color="green" size="xs">
              Сохранить порядок
            </Button>
          )}
          <Button onClick={() => openBannerModal()} variant="outline" size="xs">
            Добавить баннер
          </Button>
        </Group>
      </Group>
      <Text size="xs" c="dimmed" mt={4}>
        Размер главного баннера: 1300 x 500 px
      </Text>
      {bannerLoading ? (
        <Loader size="sm" mt={16} />
      ) : banners.length ? (
        <BannersGrid>
          {banners.map((banner) => (
            <BannerItem
              key={banner.id}
              draggable
              onDragStart={(e) => handleDragStart(e, banner.id)}
              onDragOver={(e) => handleDragOver(e, banner.id)}
              onDrop={(e) => handleDrop(e, banner.id)}
              onDragEnd={handleDragEnd}
              style={{
                position: "relative",
                opacity: draggedId === banner.id ? 0.4 : 1,
                border:
                  draggedOverId === banner.id
                    ? "2px dashed #ffd93d"
                    : "1px solid #dee2e6",
                transition: "all 0.2s ease",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: "20px",
                  top: "20px",
                  zIndex: 10,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: "4px",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "grab",
                }}
              >
                <IconGripVertical size={16} color="#333" />
              </div>

              <Banner $compact background={banner.background}>
                {banner.title ? (
                  <div className="title">{banner.title}</div>
                ) : null}
                {banner.subtitle ? (
                  <div className="description">{banner.subtitle}</div>
                ) : null}
              </Banner>
              <Group mt={12} justify="space-between" align="center">
                <Group gap="xs">
                  <Button
                    onClick={() => openBannerModal(banner)}
                    variant="outline"
                    size="xs"
                  >
                    Изменить
                  </Button>
                  <Button
                    onClick={() => handleDeleteBanner(banner)}
                    color="red"
                    variant="outline"
                    size="xs"
                    loading={deleteBannerLoading}
                  >
                    Удалить
                  </Button>
                </Group>
              </Group>
            </BannerItem>
          ))}
        </BannersGrid>
      ) : (
        <Text size="sm" c="dimmed" mt={12}>
          Баннеры еще не добавлены
        </Text>
      )}
      <Group mt={20} align="center" justify="space-between">
        <Text fw="600">Логотип</Text>
      </Group>
      {referencesLoading ? (
        <Loader size="sm" mt={16} />
      ) : (
        <>
          <Logo>
            {logo?.value ? (
              <Image
                src={`/uploads/${logo.value}`}
                className="image"
                alt="Логотип"
              />
            ) : (
              <Image src="/logo.png" className="image" alt="Логотип" />
            )}
          </Logo>
          <div>
            <Button
              onClick={() => setVisibleUpdateLogo(true)}
              variant="outline"
              mt={16}
              size="xs"
            >
              Изменить логотип
            </Button>
          </div>
        </>
      )}
      <Group mt={20} align="center" justify="space-between">
        <Text fw="600">Верхний баннер</Text>
      </Group>
      {topBannerLoading ? (
        <Loader size="sm" mt={16} />
      ) : topBanner ? (
        <>
          <TopBannerPreview>
            <Image
              src={`/uploads/${topBanner.image}`}
              className="image"
              alt="TopBanner"
            />
            <Text size="sm" mt={10}>
              Ссылка: {topBanner.url}
            </Text>
          </TopBannerPreview>
          <Group mt={16}>
            <Button onClick={openTopBannerModal} variant="outline" size="xs">
              Изменить TopBanner
            </Button>
            <Button
              onClick={handleDeleteTopBanner}
              color="red"
              variant="outline"
              size="xs"
              loading={deleteTopBannerLoading}
            >
              Удалить
            </Button>
          </Group>
        </>
      ) : (
        <>
          <Text size="sm" c="dimmed" mt={12}>
            Верхний баннер еще не добавлен
          </Text>
          <div>
            <Button
              onClick={openTopBannerModal}
              variant="outline"
              mt={16}
              size="xs"
            >
              Добавить верхний баннер
            </Button>
          </div>
        </>
      )}
      <Modal
        title="Изменить пароль"
        opened={visibleUpdatePassword}
        onClose={() => setVisibleUpdatePassword(false)}
      >
        <form onSubmit={handleUpdatePassword}>
          <TextInput
            type="password"
            required
            label="Текущий пароль"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
          <TextInput
            type="password"
            required
            label="Новый пароль"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            mt={16}
          />
          <TextInput
            type="password"
            required
            label="Повторите пароль"
            value={repeatPassword}
            onChange={(e) => setRepeatPassword(e.target.value)}
            mt={16}
          />
          <Button loading={loading} type="submit" mt={20}>
            Изменить
          </Button>
        </form>
      </Modal>
      <Modal
        title={selectedBanner ? "Изменить баннер" : "Добавить баннер"}
        opened={visibleUpdateBanner}
        onClose={() => {
          setVisibleUpdateBanner(false);
          setSelectedBanner(null);
        }}
      >
        <form onSubmit={handleUpdateBanner}>
          <TextInput
            label="Заголовок"
            value={bannerTitle}
            onChange={(e) => setBannerTitle(e.target.value)}
          />
          <TextInput
            label="Подзаголовок"
            value={bannerSubtitle}
            onChange={(e) => setBannerSubtitle(e.target.value)}
            mt={16}
          />
          <FileInput
            label={selectedBanner ? "Новый задний фон" : "Задний фон"}
            description="Рекомендуемый размер: 1300 x 500 px. Изображение будет показано полностью, без обрезки."
            placeholder="Нажмите, чтобы выбрать изображение"
            mt={16}
            value={file}
            onChange={setFile}
            accept="image/png,image/jpeg,image/jpg,image/gif"
          />
          {!file && bannerDefaultBackground ? (
            <ImageContainer>
              <Image
                src={`/uploads/${bannerDefaultBackground}`}
                alt={bannerDefaultBackground}
                mt={16}
                width={200}
              />
              <div
                className="delete"
                onClick={() => {
                  setFile(null);
                  setBannerDefaultBackground(null);
                }}
              >
                <IconTrash color="red" />
              </div>
            </ImageContainer>
          ) : null}
          <Button loading={bannerUpdateLoading} type="submit" mt={20}>
            {selectedBanner ? "Изменить" : "Добавить"}
          </Button>
        </form>
      </Modal>
      <Modal
        title="Изменить логотип"
        opened={visibleUpdateLogo}
        onClose={() => setVisibleUpdateLogo(false)}
      >
        <form onSubmit={handleUpdateLogo}>
          <FileInput
            label="Новый задний фон"
            placeholder="Нажмите, чтобы выбрать изображение"
            value={logoFile}
            onChange={setLogoFile}
            required
          />
          <Button loading={logoUpdateLoading} type="submit" mt={20}>
            Изменить
          </Button>
        </form>
      </Modal>
      <Modal
        title={topBanner ? "Изменить TopBanner" : "Добавить TopBanner"}
        opened={visibleTopBannerModal}
        onClose={() => setVisibleTopBannerModal(false)}
      >
        <form onSubmit={handleTopBannerSubmit}>
          <TextInput
            required
            label="Ссылка"
            value={topBannerUrl}
            onChange={(e) => setTopBannerUrl(e.target.value)}
          />
          <FileInput
            label={topBanner ? "Новое изображение" : "Изображение"}
            description="Рекомендуемое соотношение сторон: 1300 / 90"
            placeholder="Нажмите, чтобы выбрать изображение"
            mt={16}
            value={topBannerFile}
            onChange={setTopBannerFile}
            required={!topBannerDefaultImage}
          />
          {!topBannerFile && topBannerDefaultImage ? (
            <ImageContainer>
              <Image
                src={`/uploads/${topBannerDefaultImage}`}
                alt={topBannerDefaultImage}
                mt={16}
                width={200}
              />
            </ImageContainer>
          ) : null}
          <Button
            loading={topBannerSubmitLoading || updateTopBannerLoading}
            type="submit"
            mt={20}
          >
            {topBanner ? "Изменить" : "Добавить"}
          </Button>
        </form>
      </Modal>
    </>
  );
};

const BannersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-top: 12px;
`;

const BannerItem = styled.div`
  padding: 12px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
`;

const Banner = styled.div`
  width: 100%;
  aspect-ratio: 1300 / 500;
  min-height: ${({ $compact }) => ($compact ? "120px" : "180px")};
  background-color: #f5f0e8;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-left: 40px;
  padding-right: 40px;
  margin-top: 12px;

  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;

  ${(props) =>
    props.background
      ? `
        background-image: url('/uploads/${props.background}');
      `
      : ""}

  @media only screen and (max-width: 650px) {
    width: 100%;
    padding-left: 30px;
    padding-right: 30px;
  }

  @media only screen and (max-width: 650px) {
    grid-area: 1 / 1 / 2 / 3;
    min-height: 100px;
  }

  @media only screen and (max-width: 400px) {
    min-height: 90px;
  }

  .title {
    font-size: 30px;
    color: #232323;
    font-weight: 600;
    width: 100%;
    line-height: 30px;

    @media only screen and (max-width: 800px) {
      font-size: 26px;
    }

    @media only screen and (max-width: 500px) {
      font-size: 22px;
      line-height: 26px;
    }
  }

  .description {
    font-size: 16px;
    color: #626262;
    font-weight: 500;
    margin-top: 12px;
    width: 100%;

    @media only screen and (max-width: 800px) {
      font-size: 14px;
    }

    @media only screen and (max-width: 500px) {
      margin-top: 6px;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;

  .delete {
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
    transition: 0.3s opacity;

    &:hover {
      opacity: 0.8;
    }
  }
`;

const Logo = styled.div`
  margin-top: 12px;

  .image {
    width: 200px;
  }
`;

const TopBannerPreview = styled.div`
  margin-top: 12px;

  .image {
    width: 100%;
    max-width: 420px;
    border-radius: 8px;
    border: 1px solid #dee2e6;
  }
`;
